export interface IHubProxy {
    invoke(method: string, ...args: any[]): JQueryDeferred<any>;
    on(event: string, callback: (...msg: any[]) => void): IHubProxy;
}

export interface IHubConnection {
    state: number;
    id: string;
    createHubProxy(hubName: string): IHubProxy;
    start(): JQueryPromise<any>;
    stop(): void;
    starting(cb: () => void): IHubConnection;
    received(cb: (data: any) => void): IHubConnection;
    error(cb: (error: any) => void): IHubConnection;
    stateChanged(cb: (change: { oldState: number; newState: number }) => void): IHubConnection;
    disconnected(cb: () => void): IHubConnection;
    connectionSlow(cb: () => void): IHubConnection;
    reconnecting(cb: () => void): IHubConnection;
    reconnected(cb: () => void): IHubConnection;
}

class SignalRHubConnection implements IHubConnection {
    private hub: HubConnection;

    get state(): number { return this.hub.state; }
    set state(value: number) { this.hub.state = value; }
    get id(): string { return this.hub.id; }

    constructor(access_token: string) {
        require('signalr');
        let qs: any = {};
        if (access_token) {
            qs.access_token = access_token;
        }
        this.hub = $.hubConnection("/signalr", { qs: qs });
    }

    createHubProxy(hubName: string): IHubProxy { return this.hub.createHubProxy(hubName); }
    start(): JQueryPromise<any> { return this.hub.start(); }
    stop(): void { this.hub.stop(); }
    starting(cb: () => void): IHubConnection { this.hub.starting(cb); return this; }
    received(cb: (data: any) => void): IHubConnection { this.hub.received(cb); return this; }
    error(cb: (error: any) => void): IHubConnection { this.hub.error(cb); return this; }
    stateChanged(cb: (change: { oldState: number; newState: number }) => void): IHubConnection { this.hub.stateChanged(cb); return this; }
    disconnected(cb: () => void): IHubConnection { this.hub.disconnected(cb); return this; }
    connectionSlow(cb: () => void): IHubConnection { this.hub.connectionSlow(cb); return this; }
    reconnecting(cb: () => void): IHubConnection { this.hub.reconnecting(cb); return this; }
    reconnected(cb: () => void): IHubConnection { this.hub.reconnected(cb); return this; }
}

function generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0;
        var v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// Connection state constants matching SignalR's enum values
const ConnectionState = { Connecting: 0, Connected: 1, Reconnecting: 2, Disconnected: 4 };

// PersistentConnection reads SignalR.ConnectionState directly on both connection paths
// (real + dev-server). The SignalR client used to set window.SignalR as a side effect under
// webpack's AMD handling; Rspack routes the UMD through CommonJS, so nothing sets it now.
if (typeof (window as any).SignalR === 'undefined') {
    (window as any).SignalR = { ConnectionState: ConnectionState };
}

class DevServerHubConnection implements IHubConnection {
    state: number;
    id: string;
    private accessToken: string;
    private lifecycleCallbacks: { [event: string]: Function[] } = {
        error: [],
        stateChanged: [],
        disconnected: [],
        connectionSlow: [],
        reconnecting: [],
        reconnected: [],
        starting: [],
        received: []
    };

    constructor(access_token: string) {
        this.accessToken = access_token;
        this.id = generateGuid();
        this.state = ConnectionState.Disconnected;
    }

    createHubProxy(hubName: string): IHubProxy {
        const connectionId = this.id;
        const accessToken = this.accessToken;
        const eventHandlers: { [event: string]: Function[] } = {};

        const proxy: IHubProxy = {
            invoke(method: string, ...args: any[]): JQueryDeferred<any> {
                const deferred = $.Deferred();

                // gamePlanLoggingHub.Information is fire-and-forget
                if (hubName === 'gamePlanLoggingHub' && method === 'Information') {
                    deferred.resolve(null);
                    return deferred;
                }

                $.ajax({
                    url: '/api/devhub/invoke',
                    method: 'POST',
                    contentType: 'application/json',
                    data: JSON.stringify({
                        hub: hubName,
                        method: method,
                        args: args,
                        connectionId: connectionId
                    }),
                    headers: accessToken ? { 'Authorization': 'Bearer ' + accessToken } : {},
                    success: function (response: any) {
                        deferred.resolve(response);
                    },
                    error: function (xhr: any, status: string, error: string) {
                        console.error('[DevServerHubConnection] invoke failed:', hubName + '.' + method, status, error);
                        deferred.reject(error);
                    }
                });

                return deferred;
            },
            on(event: string, callback: (...msg: any[]) => void): IHubProxy {
                if (!eventHandlers[event]) {
                    eventHandlers[event] = [];
                }
                eventHandlers[event].push(callback);
                return proxy;
            }
        };
        return proxy;
    }

    start(): JQueryPromise<any> {
        const deferred = $.Deferred();
        this.changeState(ConnectionState.Connecting);
        this.fireLifecycle('starting');
        this.changeState(ConnectionState.Connected);
        deferred.resolve();
        return deferred;
    }

    stop(): void {
        this.changeState(ConnectionState.Disconnected);
        this.fireLifecycle('disconnected');
    }

    starting(cb: () => void): IHubConnection { this.registerLifecycle('starting', cb); return this; }
    received(cb: (data: any) => void): IHubConnection { this.registerLifecycle('received', cb); return this; }
    error(cb: (error: any) => void): IHubConnection { this.registerLifecycle('error', cb); return this; }
    stateChanged(cb: (change: { oldState: number; newState: number }) => void): IHubConnection { this.registerLifecycle('stateChanged', cb); return this; }
    disconnected(cb: () => void): IHubConnection { this.registerLifecycle('disconnected', cb); return this; }
    connectionSlow(cb: () => void): IHubConnection { this.registerLifecycle('connectionSlow', cb); return this; }
    reconnecting(cb: () => void): IHubConnection { this.registerLifecycle('reconnecting', cb); return this; }
    reconnected(cb: () => void): IHubConnection { this.registerLifecycle('reconnected', cb); return this; }

    private registerLifecycle(event: string, callback: Function): void {
        if (this.lifecycleCallbacks[event]) {
            this.lifecycleCallbacks[event].push(callback);
        }
    }

    private fireLifecycle(event: string, data?: any): void {
        const cbs = this.lifecycleCallbacks[event];
        if (cbs) {
            for (let i = 0; i < cbs.length; i++) {
                try {
                    cbs[i](data);
                } catch (e) {
                    console.error('[DevServerHubConnection] lifecycle callback error:', event, e);
                }
            }
        }
    }

    private changeState(newState: number): void {
        const oldState = this.state;
        this.state = newState;
        this.fireLifecycle('stateChanged', { oldState: oldState, newState: newState });
    }
}

export class SignalrConnection {
    connection: IHubConnection;

    constructor(auth_token: string) {
        if (typeof __gameplan_is_prototype_dev_server !== 'undefined' && __gameplan_is_prototype_dev_server) {
            this.connection = new DevServerHubConnection(auth_token);
        } else {
            this.connection = new SignalRHubConnection(auth_token);
        }
    }
}

interface ConnectedInfo {
    state: "pending" | "started" | "rejected" | "resolved";
    deferred: JQueryDeferred<boolean>;
};

interface ConnectionCallback {
    statusChanged?: (connection: SignalrConnection, slow: boolean | null) => void;
}

function debug(text: string) {
    // uncomment this to see what signalr is doing.
    // console.log(text);
}

export class PersistentConnection {
    private disposed = false;
    private connection: SignalrConnection;

    public isConnected: boolean;

    private callbacks: ConnectionCallback[] = [];

    private connectedInfo: ConnectedInfo = {
        state: "pending",
        deferred: $.Deferred()
    };

    constructor(private name: string, private access_token: string, private onInit: (conn: SignalrConnection) => void) {
        this.connectedInfo = {
            state: "pending",
            deferred: $.Deferred()
        };
        this.connectedInfo.deferred.reject("not yet initialised");

        this.connection = new SignalrConnection(this.access_token);
        this.onInit(this.connection);
        this.connection.connection.starting(() => { debug(`signalr ${this.name} - starting`); });
        this.connection.connection.received(data => { debug(`signalr ${this.name} - received`); });
        this.connection.connection.error(error => {
            debug(`signalr ${this.name} - error`);
            // if we get an error, and we are disconnected, put us back into the pending state so we can try again later
            if (this.connectedInfo.state === 'started') {
                this.connectedInfo.state = "pending";
            }
        });
        this.connection.connection.stateChanged(change => {
            debug(`signalr ${this.name} - state changed ${change.oldState} -> ${change.newState}`);
            if (change.newState === SignalR.ConnectionState.Reconnecting) {
                this.resetDeferred();
            } else if (change.newState === SignalR.ConnectionState.Connected) {
                this.resolveDeferred();
            }

            this.callbacks.forEach(c => c.statusChanged && c.statusChanged(this.connection, null));
        });
        this.connection.connection.disconnected(() => { debug(`signalr ${this.name} - disconnected`); });
        this.connection.connection.connectionSlow(() => {
            debug(`signalr ${this.name} - slow`);
            this.callbacks.forEach(c => c.statusChanged && c.statusChanged(this.connection, true));
        });
        this.connection.connection.reconnecting(() => { debug(`signalr ${this.name} - reconnecting`); });
        this.connection.connection.reconnected(() => { debug(`signalr ${this.name} - reconnected`); });
    }

    statusChanged(callback: (connection: SignalrConnection, slow: boolean | null) => void) {
        this.callbacks.push({ statusChanged: callback });
    }

    dispose() {
        if (this.disposed) return;
        this.connection.connection.stop();
        this.disposed = true;
        if (this.connectedInfo.deferred.state() === 'pending') {
            this.connectedInfo.deferred.reject("Connection disposed");
        }
    }

    public ensureConnection() {
        if (this.disposed) {
            return $.Deferred().reject("Connection disposed").promise();
        }

        debug(`SignalR connection state is ${this.connection.connection.state}`);
        if (this.connection.connection.state === SignalR.ConnectionState.Disconnected && this.connectedInfo.state !== "started") {

            this.resetDeferred();

            // no need to attach
            this.connectedInfo.state = "started";
            this.connection.connection.start()
                .done(() => debug("connection.start() - done"))
                .fail(reason => debug(`connection.start() - catch - ${reason}`));
        }

        return this.connectedInfo.deferred.promise();
    }

    resolveDeferred(): void {
        if (this.connectedInfo.deferred.state() === 'pending') {
            this.connectedInfo.deferred.resolve(true);
        }
    }


    private resetDeferred() {
        if (this.connectedInfo.deferred.state() === 'pending') {
            this.connectedInfo.deferred.reject("connection restarted");
        }
        let newInfo: ConnectedInfo = {
            deferred: $.Deferred<boolean>(),
            state: "pending",
        };
        newInfo.deferred.always(() => newInfo.state = newInfo.deferred.state())
        this.connectedInfo = newInfo;
    }
}
